import React, { FC } from 'react'
// PRO-130: a deep `antd/es/*` subpath, NOT the `antd` root barrel — a seam
// module must never weld antd's whole component surface onto a chunk.
import AntdPopconfirm from 'antd/es/popconfirm'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { PopconfirmShadcn } from './Popconfirm.shadcn'
import type { SeamPopconfirmProps } from './props'

/**
 * Compat seam for antd's `Popconfirm`, behind the design toggle.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — the shipped default, so every page load of every
 * customer who has not opted in. In the `'antd'` branch this component renders
 * antd's own Popconfirm with the props it was given: same element, same class
 * names, same DOM. Like `../Button` / `../Tooltip` / `../Menu` and unlike the
 * six appearance shadows, `Popconfirm` was NEVER shadowed before this port —
 * `export * from 'antd'` handed all 45 call sites antd's own — so the parity
 * baseline is `antd/es/popconfirm` itself, asserted node for node in
 * `../__tests__/popconfirm-seam-parity.test.tsx`.
 *
 * The ONE thing the antd branch must do beyond forwarding: strip `modal`.
 * It is this seam's own prop (see `./props.ts`), and antd's Popconfirm spreads
 * its rest props onto `Popover` → rc-tooltip → the DOM, so letting it through
 * would put a stray attribute on customers' pages.
 *
 * ---------------------------------------------------------------------------
 * TWO SHAPES, ONE SEAM
 *
 * The shadcn branch renders an anchored popover by default and a centred
 * `AlertDialog` when a call site passes `modal`. That split is a captain's
 * ruling, not a taste call, and the reasoning — plus the rule for choosing
 * between them — is in `./Popconfirm.shadcn.tsx`'s header. Read it before
 * adding a call site.
 *
 * 45 files reach this name through the `src/antd` barrel and none of them
 * imports raw antd, so the barrel's explicit re-export is what makes the
 * toggle reach all 59 tags.
 */
const PopconfirmDispatcher: FC<SeamPopconfirmProps> = (props) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <PopconfirmShadcn {...props} />

  const { modal: _modal, ...antdProps } = props
  return <AntdPopconfirm {...antdProps} />
}

PopconfirmDispatcher.displayName = 'Popconfirm'

export const Popconfirm = PopconfirmDispatcher

export type { SeamPopconfirmProps }

export default PopconfirmDispatcher
